import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function BillingSuccess() {
  const { refreshBilling, billingReady } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // Confirm synchronously against Stripe first, so we don't depend on
  // webhook timing at all in the common case.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    fetch('/api/billing/confirm-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(r => r.json())
      .then(() => refreshBilling())
      .catch(() => {}); // falls through to the polling loop below if this fails
  }, []);

  // Poll billing status until active/trialing with stripe_sub_id —
  // stays as a fallback for webhook delay or confirm-checkout failing.
  useEffect(() => {
    const interval = setInterval(async () => {
      await refreshBilling();
      setAttempts(a => a + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (billingReady) {
      navigate('/dashboard', { replace: true });
    }
  }, [billingReady]);

  useEffect(() => {
    if (attempts >= 15) {
      setTimedOut(true);
    }
  }, [attempts]);

  if (timedOut) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:24 }}>
        <div style={{ textAlign:'center', maxWidth:420 }}>
          <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
          <h2 style={{ fontSize:24, fontWeight:300, marginBottom:12 }}>Taking longer than expected</h2>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:24 }}>
            Your payment was received but we're still setting up your account. This usually takes a few seconds.
          </p>
          <button
            onClick={() => { setTimedOut(false); setAttempts(0); }}
            style={{ padding:'12px 24px', background:'var(--ink)', color:'#fff', border:'none', borderRadius:8, fontSize:15, cursor:'pointer', marginRight:12 }}
          >
            Try again
          </button>
          <a href="mailto:contact@landlordcomply.uk" style={{ fontSize:13, color:'var(--accent)' }}>
            Contact support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:420 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
        <h2 style={{ fontSize:28, fontWeight:300, marginBottom:8 }}>
          Welcome to Landlord<em>Comply</em>
        </h2>
        <p style={{ color:'var(--ink-3)', fontSize:15, marginBottom:32 }}>
          Setting up your account…
        </p>
        <div style={{ display:'flex', justifyContent:'center', gap:6 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: 'var(--accent)',
              animation: `bounce 1.2s ${i * 0.2}s infinite ease-in-out`,
            }} />
          ))}
        </div>
        <style>{`
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}